'use client'

import { Alert, Stack } from '@mui/material'
import type { FileUIPart } from 'ai'
import { useTranslations } from 'next-intl'
import { useCallback, useRef, useState } from 'react'

import { useCreateConversationMutation } from '~/data/api/client/assistant'
import { logger } from '~/debug/logger'
import type { FocusedContextRef } from '~/features/assistant/contextRef'

import Composer from './Composer'
import EmptyState from './EmptyState'
import AttachmentDropZone from './attachments/AttachmentDropZone'
import useAttachmentUploads from './attachments/useAttachmentUploads'
import type { ComposedMessage } from './contextRefs'

type NewConversationProps = {
  onCreated: (conversationId: string, firstMessage: ComposedMessage, files: FileUIPart[]) => void
}

/**
 * The empty state. The conversation is created when the first message is sent, so abandoned chats leave nothing,
 * or earlier when a file is attached, since an upload belongs to a conversation; a chat abandoned after attaching
 * a file stays in the history without messages.
 */
export default function NewConversation({ onCreated }: NewConversationProps) {
  const t = useTranslations('features.AssistantV2')
  const [createConversation, { isLoading, isError }] = useCreateConversationMutation()
  const [contextRefs, setContextRefs] = useState<FocusedContextRef[]>([])
  // One conversation per empty state, however many files are attached before the first message.
  const conversation = useRef<Promise<string>>(undefined)

  const ensureConversation = useCallback(() => {
    conversation.current ??= createConversation({ conversationCreateParams: {} })
      .unwrap()
      .then((created) => created.id)
      .catch((error: unknown) => {
        conversation.current = undefined
        throw error
      })
    return conversation.current
  }, [createConversation])

  const uploads = useAttachmentUploads(ensureConversation)

  const start = async (text: string) => {
    if (isLoading) return
    try {
      onCreated(await ensureConversation(), { text, contextRefs }, uploads.files)
    } catch (error) {
      logger.error('Failed to create an assistant conversation', error)
    }
  }

  return (
    <AttachmentDropZone onFiles={uploads.add} disabled={isLoading}>
      <Stack sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        <EmptyState onSuggestion={isLoading || uploads.isUploading ? undefined : (text) => void start(text)} />
      </Stack>
      <Stack spacing={1} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        {isError && <Alert severity="error">{t('createError')}</Alert>}
        <Composer
          onSend={(text) => void start(text)}
          disabled={isLoading}
          contextRefs={contextRefs}
          onContextRefsChange={setContextRefs}
          attachments={uploads.attachments}
          onAttach={uploads.add}
          onRemoveAttachment={uploads.remove}
        />
      </Stack>
    </AttachmentDropZone>
  )
}
